import type { EntityClient } from 'shared/channels/entity';
import type { ChannelContractShape } from 'shared/channels/contract';
import type { ChannelUnsubscribe } from 'shared/channels';
import type { Message } from 'shared/domain/messages/message';

/** How a store reacts to an event of its entity channel. */
export enum EntityEventReaction {
  /** The event carries the whole collection after a sync. */
  applyCollection = 'applyCollection',
  /** Something changed: read the collection again. */
  reload = 'reload',
  /** The download failed: keep the items, stop loading. */
  stopLoading = 'stopLoading',
  ignore = 'ignore',
}

/** Outcome of `classifyEntityEvent`; only a collection carries items. */
export type EntityEventOutcome<TModel> =
  | { reaction: EntityEventReaction.applyCollection; items: TModel[] }
  | {
      reaction: EntityEventReaction.reload | EntityEventReaction.stopLoading | EntityEventReaction.ignore;
    };

/** The read side of an entity client that a store needs. */
export type EntityCollectionClient<TModel> = Pick<
  EntityClient<TModel, ChannelContractShape['events'], ChannelContractShape['requests']>,
  'getAll' | 'onEvent' | 'readCollection'
>;

/** Another entity whose changes alter this collection (e.g. company labels shown in contracts). */
export type RelatedEntityClient = Pick<EntityCollectionClient<unknown>, 'onEvent' | 'readCollection'>;

export interface EntityCollectionConfig<TModel> {
  client: EntityCollectionClient<TModel>;
  /** For logs, e.g. `sites`. */
  entityName: string;
  /** The collection is read again after changes of these entities. */
  reloadOnChangesOf?: readonly RelatedEntityClient[];
  /** Called when a read failed or the worker's download failed (the items are kept). */
  onLoadFailed?: () => void;
  /**
   * `true` for a change after which the collection is not read again (a
   * collection the worker sends is still applied); the store reloads itself
   * later. For views saving many changes in a row (inspection atomic save).
   */
  skipReloadAfter?: (event: Message) => boolean;
}

/** Store actions of a collection served by an entity client (`createEntityCollectionActions`). */
export interface EntityCollectionActions<TModel> {
  /** Reads the collection from the worker; resolves with it, or `undefined` when the read failed. */
  reload: () => Promise<TModel[] | undefined>;
  /** Reloads once the worker's config is ready (not during logout). */
  requestReload: () => void;
  /** Follows the entity's events (and those of `reloadOnChangesOf`); returns the unsubscribe. */
  subscribeToChannelMessages: () => ChannelUnsubscribe;
  /** Loads the collection of the selected project; returns the close (drops pending reads, resets). */
  openCollection: () => () => void;
}
